import classnames from 'classnames';
import type { CSSProperties, ComponentProps } from 'react';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { css, keyframes } from '@sorare/css';

import { AnimatedNumber } from '#src/atoms/animations/AnimatedNumber/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconHourglass } from '#src/atoms/icons/system/iconHourglass.ts';
import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { BrandS } from '#src/atoms/typography/index.tsx';
import { Cost } from '#src/components/player/Cost/index.tsx';
import { useIsMobileApp } from '#src/hooks/useIsMobileApp.ts';
import { fantasy, playerGameStatusLabels } from '#src/lib/glossary.ts';
import type { Color } from '#src/style/types.ts';

// Scales keep the 37 / 24 aspect ratio: `scale(s, (s - 1) * 37 / 24 + 1)`.
const base = keyframes`
0%,
9% {
  transform: scale(1);
}
10%,
42% {
  background: var(--color);
  transform: scale(1.2, 1.3083333333333333);
}
43% {
  background: radial-gradient(transparent 0%, var(--color) 90%, var(--color) 100%);
}
66% {
  transform: scale(1.5, 1.7708333333333335);
  background: radial-gradient(transparent 0%, var(--color) 90%, var(--color) 100%);
}
70%,
82% {
  transform: scale(1);
  background: var(--color);
}
92% {
  transform: scale(1.2, 1.3083333333333333);
}
100% {
  transform: scale(1);
}
`;

const base2 = keyframes`
0%,
9% {
  transform: scale(1);
}
10%,
42% {
  background: var(--color);
  transform: scale(1.2);
}
43% {
  background: radial-gradient(transparent 0%, var(--color) 90%, var(--color) 100%);
}
66% {
  transform: scale(1.5);
  background: radial-gradient(transparent 0%, var(--color) 90%, var(--color) 100%);
}
70%,
82% {
  transform: scale(1);
  background: var(--color);
}
92% {
  transform: scale(1.2);
}
100% {
  transform: scale(1);
}
`;

const pulse = keyframes`
  from {
    transform: scale(1.4, 1.6166666666666667);
  }
  to {
    transform: scale(1.6, 1.9250000000000003);
    opacity: 0;
  }
`;
const bounce = keyframes`
  34% {
    transform: translateY(0);
  }
  53% {
    transform: translateY(-20%);
  }
  82% {
    transform: translateY(10%);
  }
  90% {
    transform: translateY(0);
  }
`;

// Shared by the pill and the hexagon blocks below.
const runAnimationRules = `
  &.runAnimation {
    animation: 1.3s cubic-bezier(0.91, 0, 0.31, 0.62) ${bounce} forwards;
    &::before {
      position: absolute;
      z-index: -1;
      inset: 0;
      background: var(--color);
      animation: 1.3s linear ${base} forwards;
      border-radius: inherit;
      content: '';
    }
    i {
      position: absolute;
      inset: 0;
      z-index: -1;

      &,
      &::before,
      &::after {
        border-radius: inherit;
      }
      &::after,
      &::before {
        position: absolute;
        inset: 0;
        background: radial-gradient(
          transparent 0%,
          var(--color) 90%,
          var(--color) 100%
        );
        content: '';
        border-radius: inherit;
        animation: 0.34s ease ${pulse} forwards 0.19s;
      }
      &::after {
        animation-delay: 0.41s;
      }
    }
  }
`;

const root = css`
  --color: #f7aa38;
  isolation: isolate;
  position: relative;
  border-radius: 2em;
  text-align: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--half-unit);
  height: var(--triple-unit);
  padding: 0 var(--unit);
  background: var(--color);
  color: var(--c-onBase-light);
  transform: translateY(0);
  letter-spacing: -1px; /** Counter the tabular-nums spacing while keeping the same monospace aspect */
  font-variant: tabular-nums;
  > span {
    min-width: 2ch;
  }
  ${runAnimationRules}
`;
const text = css`
  font-family: var(--brand-font);
`;
const cost = css`
  display: inline-flex;
  ${runAnimationRules}
  &.runAnimation {
    &::before {
      inset: 0 2px 0 2px;
      clip-path: path(
        'M2.015 6.848A4 4 0 0 0 0 10.321V21.68a4 4 0 0 0 2.015 3.473l10 5.714a4 4 0 0 0 3.97 0l10-5.714A4 4 0 0 0 28 21.679V10.32a4 4 0 0 0-2.015-3.473l-10-5.714a4 4 0 0 0-3.97 0l-10 5.714Z'
      );
      animation: 1.3s linear ${base2} forwards;
    }
  }
`;
const arrows = css`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: -1;
  path {
    fill: #ff5600;
  }
  &.up {
    transform: translate(-50%, -50%) rotate(180deg);
    path {
      fill: #00ff46;
    }
  }
`;

const ArrowsWrapper = ({ up }: { up: boolean }) => {
  return (
    <svg
      className={classnames(arrows, { up })}
      height="50"
      viewBox="0 0 45 35"
      fill="none"
    >
      {[0, 1, 2].map(id => (
        <path key={id}>
          <animate
            attributeName="d"
            begin={id * 0.15}
            dur=".46"
            values="M16 4V1.33333L22.5 0L29 1.33333V4L22.5 2.66667L16 4Z;M16 4.00004V1.33337L22.5 1.33333L29 1.33337V4.00004L22.5 4L16 4.00004Z;M8 24.5106V10L22.5 16.7872L37 10V24.5106L22.5 32L8 24.5106Z;M16 33.3217V30.655L22.5 32.3217L29 30.655V33.3217L22.5 34.9884L16 33.3217Z"
            keyTimes="0; 0.2; 0.5; 1"
          />
        </path>
      ))}
    </svg>
  );
};

type PillProps = {
  shape?: 'PILL';
  color: Color;
};
type HexagonProps = {
  shape?: 'HEXAGON';
  color?: Color;
};
type TextProps = {
  shape?: 'TEXT';
  color?: Color;
};

export type Props = {
  score: number;
  reviewing?: boolean;
  ariaLabel?: string;
  delayMs?: number;
  animated?: boolean;
  withSuffix?: boolean;
  glow?: boolean;
} & Pick<
  ComponentProps<typeof Cost>,
  'size' | 'inverted' | 'invalid' | 'noBorder'
> &
  (PillProps | HexagonProps | TextProps);

const DEFAULT_ANIMATION_DURATION = 3000;

export const Scoring = ({
  color,
  score,
  reviewing,
  ariaLabel,
  shape = 'PILL',
  delayMs,
  animated = true,
  withSuffix,
  size = 4,
  inverted,
  invalid,
  noBorder,
  glow,
}: Props) => {
  const [runAnimation, setRunAnimation] = useState<null | 'up' | 'down'>(null);
  const [displayedScore, setDisplayedScore] = useState(score);
  const { isFlutterApp, postMessage } = useIsMobileApp();
  const { formatMessage } = useIntl();

  useEffect(() => {
    if (score === displayedScore) {
      return () => {};
    }

    const animationTimeout =
      typeof delayMs === 'number'
        ? delayMs
        : Math.random() * (animated ? DEFAULT_ANIMATION_DURATION : 0);

    const clear = setTimeout(() => {
      if (isFlutterApp) {
        postMessage('animateScore', { score, previousScore: displayedScore });
      }
      setRunAnimation(score > displayedScore ? 'up' : 'down');
      setDisplayedScore(score);
    }, animationTimeout);

    return () => {
      clearTimeout(clear);
    };
  }, [displayedScore, score, isFlutterApp, postMessage, delayMs, animated]);

  useEffect(() => {
    if (!runAnimation) {
      return () => {};
    }

    const clear = setTimeout(() => {
      setRunAnimation(null);
    }, 1300);

    return () => {
      clearTimeout(clear);
    };
  }, [runAnimation]);

  const numberComponent = (
    <AnimatedNumber value={displayedScore} disableAnimationOnFirstRender />
  );
  const content = (
    <>
      {!!reviewing && (
        <FontAwesomeIcon
          icon={iconHourglass}
          size="xs"
          aria-label={formatMessage(playerGameStatusLabels.reviewing)}
        />
      )}

      {withSuffix ? (
        <Horizontal gap={0}>
          <FormattedMessage
            {...fantasy.pointsWithoutFormat}
            values={{ score: numberComponent }}
          />
        </Horizontal>
      ) : (
        numberComponent
      )}
      {!!animated && !!runAnimation && (
        <>
          <i />
          <ArrowsWrapper up={runAnimation === 'up'} />
        </>
      )}
    </>
  );

  if (shape === 'TEXT') {
    return (
      <span
        style={
          {
            '--text-gradient-color': color,
          } as CSSProperties
        }
        className={classnames(text, 'text-center text-gradient-color', {
          'max-score-glow': glow,
        })}
      >
        {content}
      </span>
    );
  }

  if (shape === 'PILL') {
    return (
      <BrandS
        bold
        style={
          {
            '--color': color,
            transform: `scale(${size / 4})`,
          } as CSSProperties
        }
        className={classnames(root, {
          runAnimation: animated && runAnimation,
          'max-score-glow': glow,
        })}
        as="div"
        aria-label={ariaLabel}
      >
        {content}
      </BrandS>
    );
  }

  return (
    <Cost
      className={classnames(cost, {
        runAnimation: animated && runAnimation,
        'max-score-glow': glow,
      })}
      score={content}
      color={color}
      style={{ '--color': color } as CSSProperties}
      size={size}
      inverted={inverted}
      invalid={invalid}
      noBorder={noBorder}
    />
  );
};
